import type { ApolloClient } from '@apollo/client';
import { browserLogger } from '@newfront-insurance/dd-rum';

import type { UIMessageWithMetadata } from '../../../lib/types';
import { GET_CHAT_WITH_MESSAGES, type GetChatWithMessagesMessage } from '../../../queries/chats';

type CacheableUIMessage = UIMessageWithMetadata & {
  uuid?: string;
  chatUuid?: string;
  createdAt?: string | Date;
};

/**
 * Normalizes a chat UUID by removing the 'new-chat-' prefix if present.
 * This ensures consistent UUID handling across the codebase.
 */
export function normalizeChatUuid(chatUuid: string): string {
  return chatUuid.replace('new-chat-', '');
}

function buildOptimisticFileUuid(messageId: string, fileIndex: number): string {
  return `${messageId}-file-${fileIndex}`;
}

/**
 * Updates the Apollo cache so an answered clarification round is visible
 * immediately on the next hydration, before the server snapshot catches up.
 */
export function markClarificationResolvedInCache({
  apolloClient,
  chatUuid,
  messageUuid,
  message,
  toolCallId,
  output,
  transcript,
}: {
  apolloClient: ApolloClient<unknown>;
  chatUuid: string;
  messageUuid: string;
  message?: CacheableUIMessage;
  toolCallId: string;
  output: unknown;
  transcript: string;
}): void {
  const targetMessageIds = new Set(
    [messageUuid, message?.id, message?.uuid].filter(
      (candidate): candidate is string => typeof candidate === 'string' && candidate.length > 0,
    ),
  );

  for (const cacheUuid of new Set([normalizeChatUuid(chatUuid), chatUuid])) {
    try {
      const cachedData = apolloClient.readQuery({
        query: GET_CHAT_WITH_MESSAGES,
        variables: { uuid: cacheUuid },
      });

      if (!cachedData?.getChat?.messages) {
        continue;
      }

      let matchedExistingMessage = false;
      const updatedMessages = cachedData.getChat.messages.map((cachedMessage) => {
        const cachedMessageUuid = typeof cachedMessage.uuid === 'string' ? cachedMessage.uuid : undefined;

        if (!cachedMessageUuid || !targetMessageIds.has(cachedMessageUuid)) {
          return cachedMessage;
        }

        matchedExistingMessage = true;

        return {
          ...cachedMessage,
          parts: resolveClarificationParts(cachedMessage.parts, toolCallId, output),
          metadata: resolveClarificationMetadata(cachedMessage.metadata, toolCallId, transcript),
        };
      });

      const nextMessages =
        !matchedExistingMessage && message
          ? [
              ...updatedMessages,
              buildClarificationCacheMessage({
                chatUuid,
                message,
                toolCallId,
                output,
                transcript,
              }),
            ]
          : updatedMessages;

      apolloClient.writeQuery({
        query: GET_CHAT_WITH_MESSAGES,
        variables: { uuid: cacheUuid },
        data: {
          getChat: {
            ...cachedData.getChat,
            messages: nextMessages,
          },
        },
      });
    } catch (error) {
      browserLogger.debug('[Notification Helpers] Could not mark clarification as resolved', {
        error,
        chatUuid,
        cacheUuid,
        messageUuid,
        toolCallId,
      });
    }
  }
}

function resolveClarificationParts(parts: unknown, toolCallId: string, output: unknown) {
  return Array.isArray(parts)
    ? parts.map((part) => {
        if (!part || typeof part !== 'object' || part.type !== 'tool-clarification' || part.toolCallId !== toolCallId) {
          return part;
        }

        return {
          ...part,
          state: 'output-available',
          output,
        };
      })
    : parts;
}

function resolveClarificationMetadata(metadata: unknown, toolCallId: string, transcript: string) {
  const existingMetadata = metadata && typeof metadata === 'object' ? (metadata as Record<string, unknown>) : {};
  const existingClarification =
    existingMetadata.clarification && typeof existingMetadata.clarification === 'object'
      ? (existingMetadata.clarification as Record<string, unknown>)
      : {};

  return {
    ...existingMetadata,
    clarification: {
      ...existingClarification,
      state: 'clarification-resolved',
      activeToolCallId: toolCallId,
      resolvedTranscript: transcript,
    },
  };
}

function buildClarificationCacheMessage({
  chatUuid,
  message,
  toolCallId,
  output,
  transcript,
}: {
  chatUuid: string;
  message: CacheableUIMessage;
  toolCallId: string;
  output: unknown;
  transcript: string;
}): GetChatWithMessagesMessage {
  const rawMetadata =
    message.metadata && typeof message.metadata === 'object' ? (message.metadata as Record<string, unknown>) : {};
  const { reaction, ...backendMetadata } = rawMetadata;
  const files = rawMetadata.files;
  const normalizedReaction: GetChatWithMessagesMessage['reaction'] =
    reaction === 'THUMBS_UP' || reaction === 'THUMBS_DOWN' ? reaction : null;
  const messageCreatedAt =
    typeof message.createdAt === 'string'
      ? message.createdAt
      : message.createdAt instanceof Date
        ? message.createdAt.toISOString()
        : new Date().toISOString();
  const messageId = message.uuid ?? message.id;

  return {
    uuid: messageId,
    chatUuid: message.chatUuid ?? normalizeChatUuid(chatUuid),
    reaction: normalizedReaction,
    role: message.role,
    files: Array.isArray(files)
      ? files.map((file, fileIndex) => ({
          uuid:
            file && typeof file === 'object' && typeof file.uuid === 'string'
              ? file.uuid
              : buildOptimisticFileUuid(messageId, fileIndex),
          filename: file && typeof file === 'object' && typeof file.filename === 'string' ? file.filename : undefined,
        }))
      : [],
    parts: resolveClarificationParts(message.parts, toolCallId, output),
    metadata: resolveClarificationMetadata(backendMetadata, toolCallId, transcript),
    createdAt: messageCreatedAt,
  };
}
